/* eslint-disable no-unused-vars */
import { useState, useRef } from "react";
import { sanitizeText } from './utils/sanitize';
import { useWorkspace } from './WorkspaceContext';
import {
  Upload, Star, FileText, X, Loader2, CheckCircle2,
  AlertCircle, Search, TrendingUp, DollarSign, Users,
  AlertTriangle, SlidersHorizontal, Save, ChevronDown,
  ChevronUp, Target, Lightbulb, Globe,
  Briefcase, Swords, HandCoins, ChevronRight
} from "lucide-react";

const DAILY_LIMIT = 50;
const CRITERIA_KEY = "binos_pitch_criteria";

function getTodayKey() { return `binos_usage_${new Date().toISOString().slice(0, 10)}`; }
function getUsageCount() { try { return parseInt(localStorage.getItem(getTodayKey()) || "0"); } catch { return 0; } }
function incrementUsage() { try { const c = getUsageCount() + 1; localStorage.setItem(getTodayKey(), c.toString()); return c; } catch { return 0; } }
function getSavedCriteria() { try { return localStorage.getItem(CRITERIA_KEY) || ""; } catch { return ""; } }
function saveCriteria(c) { try { localStorage.setItem(CRITERIA_KEY, c); } catch {} }

function friendlyError(msg = '') {
  const m = (msg || '').toLowerCase();
  if (m.includes('row-level security') || m.includes('rls') || m.includes('unauthorized'))
    return "Session expired. Please refresh and try again.";
  if (m.includes('too large') || m.includes('size'))
    return "File too large. Please try a smaller PDF.";
  if (m.includes('limit') || m.includes('too many'))
    return "Limit reached. Please try again later.";
  if (m.includes('duplicate') || m.includes('already exists'))
    return "This deck has already been processed.";
  if (m.includes('invalid') || m.includes('not found') || m.includes('fetch'))
    return "Could not reach the server. Please try again.";
  return "Analysis failed. Please try again.";
}

async function analyzePitchDeck(file, criteria) {
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

  const { supabase } = await import('./supabaseClient');
  const { data: { session } } = await supabase.auth.getSession();

  const response = await fetch("/api/analyze", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${session?.access_token || "anonymous"}`
    },
    body: JSON.stringify({ base64, criteria }),
  });

  const rateLimit = {
    limit: parseInt(response.headers.get("X-RateLimit-Limit") || "0", 10),
    remaining: parseInt(response.headers.get("X-RateLimit-Remaining") || "0", 10),
    used: parseInt(response.headers.get("X-RateLimit-Used") || "0", 10),
  };

  if (!response.ok) {
    const err = await response.json();
    const error = new Error(err.error || "Analysis failed");
    error.rateLimit = rateLimit;
    throw error;
  }

  const data = await response.json();
  return { data, rateLimit };
}

// LEFT PANEL — Criteria + Upload
function LeftPanel({ criteria, setCriteria, fileInputRef, multiFileInputRef, usageCount, handleFileInput }) {
  const [isOpen, setIsOpen] = useState(true);
  const [localCriteria, setLocalCriteria] = useState(criteria);
  const [saved, setSaved] = useState(false);
  const remaining = DAILY_LIMIT - usageCount;
  const usagePct = (usageCount / DAILY_LIMIT) * 100;

  const handleSave = () => {
    const sanitized = sanitizeText(localCriteria);
    setLocalCriteria(sanitized);
    setCriteria(sanitized);
    saveCriteria(sanitized);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 12, overflowY: "auto" }}>
      {/* Upload */}
      <div style={{ background: "#dc2626", borderRadius: 14, padding: "20px", cursor: "pointer", transition: "opacity 0.2s" }}
        onClick={() => multiFileInputRef.current.click()}
        onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
        onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <Upload style={{ width: 20, height: 20, color: "#fff" }} />
          <p style={{ fontSize: 14, fontWeight: 800, color: "#fff", margin: 0, letterSpacing: "-0.3px" }}>Upload Decks</p>
        </div>
        <p style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", margin: 0, fontWeight: 500 }}>PDF only · Canva · Scanned · Any format</p>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={e => { e.stopPropagation(); fileInputRef.current.click(); }} style={{ flex: 1, padding: "7px", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Single</button>
          <button onClick={e => { e.stopPropagation(); multiFileInputRef.current.click(); }} style={{ flex: 1, padding: "7px", background: "rgba(255,255,255,0.25)", border: "none", borderRadius: 8, fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Bulk</button>
        </div>
        <input ref={fileInputRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={handleFileInput} />
        <input ref={multiFileInputRef} type="file" accept=".pdf" multiple style={{ display: "none" }} onChange={handleFileInput} />
      </div>

      {/* Usage bar */}
      <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "14px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>Daily Usage</p>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {remaining <= 10 && <AlertTriangle style={{ width: 11, height: 11, color: "#d97706" }} />}
            <p style={{ fontSize: 10, fontWeight: 700, color: remaining <= 10 ? "#d97706" : "rgba(255,255,255,0.4)", margin: 0 }}>{usageCount}/{DAILY_LIMIT}</p>
          </div>
        </div>
        <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 100, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${usagePct}%`, background: usagePct > 80 ? "#dc2626" : usagePct > 50 ? "#d97706" : "#16a34a", borderRadius: 100, transition: "width 0.5s ease" }} />
        </div>
        <p style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", margin: "8px 0 0", fontWeight: 500 }}>{remaining} decks remaining today</p>
      </div>

      {/* Criteria filter */}
      <div style={{ background: "#1a1a1a", border: criteria ? "1px solid rgba(220,38,38,0.3)" : "1px solid rgba(255,255,255,0.06)", borderRadius: 14, overflow: "hidden" }}>
        <button onClick={() => setIsOpen(!isOpen)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <SlidersHorizontal style={{ width: 15, height: 15, color: criteria ? "#dc2626" : "rgba(255,255,255,0.3)" }} />
            <div style={{ textAlign: "left" }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: "#fff", margin: 0 }}>Investment Filter</p>
              <p style={{ fontSize: 9, color: criteria ? "#dc2626" : "rgba(255,255,255,0.3)", margin: 0, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>{criteria ? "Active" : "Not set"}</p>
            </div>
          </div>
          {isOpen ? <ChevronUp style={{ width: 14, height: 14, color: "rgba(255,255,255,0.3)" }} /> : <ChevronDown style={{ width: 14, height: 14, color: "rgba(255,255,255,0.3)" }} />}
        </button>
        {isOpen && (
          <div style={{ padding: "0 16px 16px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", margin: "12px 0 8px", fontWeight: 500, lineHeight: 1.5 }}>Describe what you're looking for. AI will score every deck against this.</p>
            <textarea value={localCriteria} onChange={e => setLocalCriteria(e.target.value)} rows={5}
              placeholder="e.g. B2B SaaS, Seed or Series A, India-focused, technical founding team, ARR above ₹2Cr..."
              style={{ width: "100%", padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, outline: "none", fontSize: 12, fontWeight: 500, color: "#fff", resize: "none", fontFamily: "inherit", lineHeight: 1.6, boxSizing: "border-box" }}
              onFocus={e => e.target.style.borderColor = "#dc2626"}
              onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "10px 0" }}>
              {["B2B SaaS, Seed/Series A, India", "Consumer fintech, pre-seed, Tier 2", "Deep tech, Series A, global market"].map((ex, i) => (
                <button key={i} onClick={() => setLocalCriteria(ex)}
                  style={{ padding: "6px 10px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.35)", cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(220,38,38,0.3)"; e.currentTarget.style.color = "#dc2626"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "rgba(255,255,255,0.35)"; }}>
                  "{ex}"
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleSave} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px", background: saved ? "#16a34a" : "#dc2626", border: "none", borderRadius: 9, fontSize: 11, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "inherit", transition: "background 0.2s" }}>
                <Save style={{ width: 12, height: 12 }} /> {saved ? "Saved!" : "Save"}
              </button>
              {localCriteria && (
                <button onClick={() => { setLocalCriteria(""); setCriteria(""); saveCriteria(""); }} style={{ padding: "9px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", cursor: "pointer", fontFamily: "inherit" }}>Clear</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// MIDDLE PANEL — Deck list
function DeckCard({ deck, isSelected, onClick }) {
  const match = deck.summary?.criteriaMatch;
  const matchColor = match?.verdict === "Strong Match" ? "#16a34a" : match?.verdict === "Partial Match" ? "#d97706" : match?.verdict === "Not a Fit" ? "#dc2626" : null;

  return (
    <div onClick={onClick}
      style={{ background: isSelected ? "rgba(220,38,38,0.08)" : "#1a1a1a", border: isSelected ? "1px solid rgba(220,38,38,0.4)" : "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "16px", cursor: deck.status === "done" ? "pointer" : "default", transition: "all 0.15s" }}
      onMouseEnter={e => { if (!isSelected && deck.status === "done") { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}}
      onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "#1a1a1a"; }}}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: deck.status === "processing" ? "rgba(255,255,255,0.05)" : deck.status === "done" ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.12)" }}>
          {deck.status === "processing" && <Loader2 style={{ width: 16, height: 16, color: "rgba(255,255,255,0.3)" }} className="animate-spin" />}
          {deck.status === "done" && <CheckCircle2 style={{ width: 16, height: 16, color: "#16a34a" }} />}
          {deck.status === "error" && <AlertCircle style={{ width: 16, height: 16, color: "#dc2626" }} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {deck.status === "done" && deck.summary ? (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{deck.summary.companyName}</p>
                {match && matchColor && (
                  <span style={{ fontSize: 9, fontWeight: 800, color: matchColor, background: matchColor + "15", padding: "2px 8px", borderRadius: 100, whiteSpace: "nowrap", flexShrink: 0 }}>{match.score}%</span>
                )}
              </div>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", margin: "0 0 10px", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{deck.summary.oneLiner}</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[deck.summary.marketSize, deck.summary.traction].map((label, i) => label && label !== "Not mentioned" && (
                  <span key={i} style={{ fontSize: 9, fontWeight: 600, color: "rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.05)", padding: "3px 8px", borderRadius: 6 }}>{label}</span>
                ))}
              </div>
            </>
          ) : deck.status === "processing" ? (
            <>
              <p style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", margin: "0 0 3px" }}>Analysing deck...</p>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{deck.fileName}</p>
            </>
          ) : (
            <>
              <p style={{ fontSize: 12, fontWeight: 700, color: "#dc2626", margin: "0 0 3px" }}>Analysis failed</p>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", margin: 0 }}>{deck.errorMsg}</p>
            </>
          )}
        </div>
        {deck.status === "done" && <ChevronRight style={{ width: 14, height: 14, color: isSelected ? "#dc2626" : "rgba(255,255,255,0.15)", flexShrink: 0, marginTop: 2 }} />}
      </div>
    </div>
  );
}

// RIGHT PANEL — Full analysis
function AnalysisPanel({ deck, onClose, onStar }) {
  const s = deck.summary;
  const match = s?.criteriaMatch;
  const matchColor = match?.verdict === "Strong Match" ? "#16a34a" : match?.verdict === "Partial Match" ? "#d97706" : "#dc2626";

  const SIGNALS = [
    { icon: AlertCircle, label: "Problem", value: s?.problem, color: "#dc2626" },
    { icon: Lightbulb, label: "Solution", value: s?.solution, color: "#d97706" },
    { icon: Globe, label: "Market Size", value: s?.marketSize, color: "#3b82f6" },
    { icon: Briefcase, label: "Business Model", value: s?.businessModel, color: "#8b5cf6" },
    { icon: TrendingUp, label: "Traction", value: s?.traction, color: "#16a34a" },
    { icon: Users, label: "Team", value: s?.team, color: "#06b6d4" },
    { icon: Swords, label: "Competition", value: s?.competition, color: "#f97316" },
    { icon: HandCoins, label: "The Ask", value: s?.ask, color: "#ec4899" },
  ];

  return (
    <div style={{ flex: 1, background: "#161616", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.15em", margin: "0 0 6px" }}>Investment Brief</p>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: "#fff", margin: "0 0 4px", letterSpacing: "-0.5px" }}>{s?.companyName}</h2>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", margin: 0, fontWeight: 400, lineHeight: 1.5 }}>{s?.oneLiner}</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button onClick={() => onStar(deck.id)} style={{ padding: "8px 10px", background: deck.starred ? "rgba(234,179,8,0.1)" : "rgba(255,255,255,0.05)", border: deck.starred ? "1px solid rgba(234,179,8,0.3)" : "1px solid rgba(255,255,255,0.08)", borderRadius: 9, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <Star style={{ width: 14, height: 14, color: deck.starred ? "#eab308" : "rgba(255,255,255,0.3)" }} fill={deck.starred ? "#eab308" : "none"} />
            {deck.starred && <span style={{ fontSize: 10, fontWeight: 700, color: "#eab308" }}>Starred</span>}
          </button>
          <button onClick={onClose} style={{ padding: "8px 10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, cursor: "pointer", color: "rgba(255,255,255,0.4)" }}>
            <X style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        {/* Criteria match */}
        {match && (
          <div style={{ background: matchColor + "10", border: `1px solid ${matchColor}25`, borderRadius: 14, padding: "16px 20px", marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Target style={{ width: 14, height: 14, color: matchColor }} />
                <p style={{ fontSize: 11, fontWeight: 800, color: matchColor, textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>Criteria Match</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20, fontWeight: 900, color: matchColor }}>{match.score}%</span>
                <span style={{ fontSize: 10, fontWeight: 800, color: matchColor, background: matchColor + "20", padding: "3px 10px", borderRadius: 100 }}>{match.verdict}</span>
              </div>
            </div>
            <div style={{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 100, overflow: "hidden", marginBottom: 12 }}>
              <div style={{ height: "100%", width: `${match.score}%`, background: matchColor, borderRadius: 100 }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {match.reasons?.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: matchColor, flexShrink: 0 }} />
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", margin: 0, fontWeight: 500 }}>{r}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 9 signals grid */}
        <p style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.12em", margin: "0 0 14px" }}>Investment Signals</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 24 }}>
          {SIGNALS.map(({ icon: Icon, label, value, color }) => (
            <div key={label} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
                <Icon style={{ width: 13, height: 13, color }} />
                <p style={{ fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>{label}</p>
              </div>
              <p style={{ fontSize: 13, fontWeight: 600, color: "#fff", margin: 0, lineHeight: 1.5 }}>{value || "Not mentioned"}</p>
            </div>
          ))}
        </div>

        {/* Ask */}
        <div style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.15)", borderRadius: 12, padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <DollarSign style={{ width: 13, height: 13, color: "#dc2626" }} />
            <p style={{ fontSize: 9, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>The Ask</p>
          </div>
          <p style={{ fontSize: 16, fontWeight: 800, color: "#fff", margin: 0, letterSpacing: "-0.3px" }}>{s?.ask || "Not mentioned"}</p>
        </div>
      </div>
    </div>
  );
}

export default function PitchSummarizer() {
  const { logActivity } = useWorkspace() || {};
  const [decks, setDecks] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStarred, setFilterStarred] = useState(false);
  const [usageCount, setUsageCount] = useState(getUsageCount());
  const [criteria, setCriteria] = useState(getSavedCriteria());
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const fileInputRef = useRef(null);
  const multiFileInputRef = useRef(null);

  const handleFiles = async (files) => {
    const filesArray = Array.from(files);
    const remaining = DAILY_LIMIT - getUsageCount();
    if (remaining <= 0) { alert("Daily limit of 50 decks reached. Resets at midnight."); return; }
    const toProcess = filesArray.slice(0, remaining);
    const newDecks = toProcess.map((file) => ({ id: Date.now() + Math.random(), fileName: file.name, status: "processing", starred: false, summary: null, errorMsg: null, file }));
    setDecks((prev) => [...newDecks, ...prev]);
    for (const deck of newDecks) {
      try {
        const { data: summary, rateLimit } = await analyzePitchDeck(deck.file, criteria);
        if (rateLimit.limit > 0) setRateLimitInfo(rateLimit);
        const newCount = incrementUsage();
        setUsageCount(newCount);
        setDecks((prev) => prev.map((d) => d.id === deck.id ? { ...d, status: "done", summary } : d));
        logActivity?.('analysed_deck', 'deck', String(deck.id), summary.companyName, { score: summary.criteriaMatch?.score, verdict: summary.criteriaMatch?.verdict });
        // RAG embedding disabled - re-enable when proper embedding model added
        // import('./supabaseClient').then(({ supabase: sb }) => {
        //   sb.auth.getSession().then(({ data: { session } }) => {
        //     const embedContent = [
        //       `Company: ${summary.companyName}`,
        //       `One liner: ${summary.oneLiner}`,
        //       `Problem: ${summary.problem}`,
        //       `Solution: ${summary.solution}`,
        //       `Market Size: ${summary.marketSize}`,
        //       `Business Model: ${summary.businessModel}`,
        //       `Traction: ${summary.traction}`,
        //       `Team: ${summary.team}`,
        //       `Competition: ${summary.competition}`,
        //       `Ask: ${summary.ask}`,
        //       `Match Score: ${summary.criteriaMatch?.score || 'Not scored'}`,
        //       `Verdict: ${summary.criteriaMatch?.verdict || 'Not scored'}`,
        //     ].join('\n');
        //     fetch('/api/rag', {
        //       method: 'POST',
        //       headers: {
        //         'Content-Type': 'application/json',
        //         'Authorization': `Bearer ${session?.access_token}`
        //       },
        //       body: JSON.stringify({
        //         operation: 'embed',
        //         content: embedContent,
        //         contentType: 'deck_analysis',
        //         companyName: summary.companyName,
        //       })
        //     }).catch(() => {});
        //   });
        // });
      } catch (err) {
        if (err.rateLimit?.limit > 0) setRateLimitInfo(err.rateLimit);
        setDecks((prev) => prev.map((d) => d.id === deck.id ? { ...d, status: "error", errorMsg: friendlyError(err.message) } : d));
      }
    }
  };

  const handleFileInput = (e) => { if (e.target.files.length > 0) handleFiles(e.target.files); };

  const toggleStar = (id) => {
    setDecks((prev) => prev.map((d) => d.id === id ? { ...d, starred: !d.starred } : d));
    if (selectedDeck?.id === id) setSelectedDeck((prev) => ({ ...prev, starred: !prev.starred }));
  };

  const removeDeck = (id) => {
    setDecks((prev) => prev.filter((d) => d.id !== id));
    if (selectedDeck?.id === id) setSelectedDeck(null);
  };

  const filtered = decks.filter((d) => {
    const matchSearch = !searchQuery || d.fileName.toLowerCase().includes(searchQuery.toLowerCase()) || d.summary?.companyName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStar = !filterStarred || d.starred;
    return matchSearch && matchStar;
  });

  const processing = decks.filter(d => d.status === "processing").length;
  const starred = decks.filter(d => d.starred).length;

  return (
    <div style={{ display: "flex", gap: 16, minHeight: "calc(100vh - 128px)", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <LeftPanel criteria={criteria} setCriteria={setCriteria} fileInputRef={fileInputRef} multiFileInputRef={multiFileInputRef} usageCount={usageCount} handleFileInput={handleFileInput} />

      {/* MIDDLE — Deck list */}
      <div style={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Server-side usage bar */}
        {rateLimitInfo && rateLimitInfo.limit > 0 && (
          <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "14px 16px" }}>
            {rateLimitInfo.remaining === 0 ? (
              <p style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", margin: 0, textAlign: "center" }}>
                Limit reached. Resets at midnight UTC.
              </p>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <p style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.5)", margin: 0 }}>
                    {rateLimitInfo.used} / {rateLimitInfo.limit} analyses used today
                  </p>
                  {rateLimitInfo.remaining < 10 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <AlertTriangle style={{ width: 11, height: 11, color: "#d97706" }} />
                      <p style={{ fontSize: 10, fontWeight: 700, color: "#d97706", margin: 0 }}>{rateLimitInfo.remaining} left</p>
                    </div>
                  )}
                </div>
                <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 100, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min((rateLimitInfo.used / rateLimitInfo.limit) * 100, 100)}%`, background: "#dc2626", borderRadius: 100, transition: "width 0.5s ease" }} />
                </div>
              </>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Search style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, color: "rgba(255,255,255,0.25)" }} />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search decks..."
              style={{ width: "100%", paddingLeft: 34, paddingRight: 12, paddingTop: 10, paddingBottom: 10, background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, outline: "none", fontSize: 12, fontWeight: 500, color: "#fff", fontFamily: "inherit", boxSizing: "border-box" }}
              onFocus={e => e.target.style.borderColor = "#dc2626"}
              onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.06)"} />
          </div>
          <button onClick={() => setFilterStarred(!filterStarred)} style={{ padding: "10px 12px", background: filterStarred ? "rgba(234,179,8,0.1)" : "#1a1a1a", border: filterStarred ? "1px solid rgba(234,179,8,0.3)" : "1px solid rgba(255,255,255,0.06)", borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center" }}>
            <Star style={{ width: 13, height: 13, color: filterStarred ? "#eab308" : "rgba(255,255,255,0.3)" }} fill={filterStarred ? "#eab308" : "none"} />
          </button>
        </div>

        {decks.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            {[{ l: "Total", v: decks.length, c: "#fff" }, { l: "Processing", v: processing, c: "#d97706" }, { l: "Starred", v: starred, c: "#eab308" }].map((s, i) => (
              <div key={i} style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "10px", textAlign: "center" }}>
                <p style={{ fontSize: 18, fontWeight: 900, color: s.c, margin: "0 0 2px", letterSpacing: "-0.5px" }}>{s.v}</p>
                <p style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", margin: 0 }}>{s.l}</p>
              </div>
            ))}
          </div>
        )}

        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.length === 0 && decks.length === 0 && (
            <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "48px 24px", textAlign: "center", flex: 1 }}>
              <FileText style={{ width: 32, height: 32, color: "rgba(255,255,255,0.1)", margin: "0 auto 12px" }} />
              <p style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.2)", margin: "0 0 4px" }}>No decks yet</p>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.15)", fontWeight: 500, margin: 0 }}>Upload PDFs to begin</p>
            </div>
          )}
          {filtered.map((deck) => (
            <div key={deck.id} style={{ position: "relative" }}>
              <DeckCard deck={deck} isSelected={selectedDeck?.id === deck.id} onClick={() => deck.status === "done" && setSelectedDeck(deck)} />
              <button onClick={(e) => { e.stopPropagation(); removeDeck(deck.id); }}
                style={{ position: "absolute", top: 8, right: 8, padding: 4, background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.2)", opacity: 0, transition: "opacity 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = "#dc2626"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "0"; }}>
                <X style={{ width: 12, height: 12 }} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT — Analysis detail */}
      {selectedDeck?.summary ? (
        <AnalysisPanel deck={selectedDeck} onClose={() => setSelectedDeck(null)} onStar={toggleStar} />
      ) : (
        <div style={{ flex: 1, background: "#161616", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.03)", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FileText style={{ width: 24, height: 24, color: "rgba(255,255,255,0.1)" }} />
          </div>
          <p style={{ fontSize: 15, fontWeight: 800, color: "rgba(255,255,255,0.2)", margin: 0, letterSpacing: "-0.3px" }}>Select a deck to analyse</p>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.12)", fontWeight: 500, margin: 0 }}>Click any processed deck on the left</p>
        </div>
      )}
    </div>
  );
}
